import { createContext, useContext, useEffect, type ReactNode } from "react";
import { isTurnstileConfigured, preloadTurnstile } from "@/components/TurnstileWidget";

interface TurnstileContextValue {
  configured: boolean;
}

const Ctx = createContext<TurnstileContextValue>({ configured: false });

export function useTurnstile() {
  return useContext(Ctx);
}

/**
 * Provides the `configured` flag to descendants. Earlier this provider
 * mounted a hidden global Turnstile widget on every page so login could
 * read a pre-warmed token without a visible challenge. That approach
 * backfired: Cloudflare started serving the visible challenge floating
 * in the corner of unrelated pages (landing, pricing, product pages)
 * because the widget was re-rendering on every router change. The fix
 * is to mount the widget INLINE inside the forms that actually need a
 * token (login popup, OTP verify step) — that way Cloudflare only ever
 * appears when the user is on a sensitive form and expects to see it.
 */
export function TurnstileProvider({ children }: { children: ReactNode }) {
  // Warm the script + initial challenge in the background as soon as the
  // app boots. The script is `async defer` so this doesn't block paint,
  // and by the time the user actually clicks "Sign in" the widget can
  // render + solve in <500ms instead of 1-2s.
  useEffect(() => {
    if (!isTurnstileConfigured) return;
    // Defer to the next idle slot to avoid contending with critical
    // boot work (hydration, first paint, font load).
    const start = () => preloadTurnstile();
    const w = typeof window !== "undefined" ? (window as any) : null;
    if (w && typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(start, { timeout: 1500 });
      return () => {
        try { w.cancelIdleCallback(id); } catch {}
      };
    }
    const t = window.setTimeout(start, 300);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <Ctx.Provider value={{ configured: isTurnstileConfigured }}>
      {children}
    </Ctx.Provider>
  );
}
